import React, { useEffect, useState } from 'react';
import { adminService, authService } from '../../services/api';
import { AdminActivityDetail, ActivityReviewRequest } from '../../types';
import { 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Box, Chip, Button, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Card, CardContent
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';

const AdminActivitiesList: React.FC = () => {
  const [activities, setActivities] = useState<AdminActivityDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingActivity, setProcessingActivity] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('dashboardData');
      localStorage.removeItem('authUser');
      navigate('/login');
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAdminActivities();
      setActivities(data);
    } catch (err) {
      console.error('Error al cargar actividades:', err);
      setError('Error al cargar las actividades');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (activityId: number) => {
    try {
      setProcessingActivity(activityId);
      const reviewData: ActivityReviewRequest = {
        activity_id: activityId,
        status: 'approved'
      };
      
      await adminService.reviewActivity(activityId, reviewData);
      
      // Actualizar la lista de actividades
      await fetchActivities();
      
    } catch (err) {
      console.error('Error al aprobar actividad:', err);
    } finally {
      setProcessingActivity(null);
    }
  };

  const handleReject = async (activityId: number) => {
    try {
      setProcessingActivity(activityId);
      const reviewData: ActivityReviewRequest = {
        activity_id: activityId,
        status: 'rejected'
      };
      
      await adminService.reviewActivity(activityId, reviewData);
      
      // Actualizar la lista de actividades
      await fetchActivities();
      
    } catch (err) {
      console.error('Error al rechazar actividad:', err);
    } finally {
      setProcessingActivity(null);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, []);

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box p={3}>
      {/* Header con botones de navegación y logout */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/dashboard')}>
          Volver al Panel
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              backgroundColor: '#ffebee'
            }
          }}
        >
          Cerrar Sesión
        </Button>
      </Box>
      
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
          <AssignmentTurnedInIcon fontSize="large" />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">Actividades pendientes</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {activities.length} actividades esperando revisión
          </Typography>
        </Box>
      </Box>

      {activities.length === 0 ? (
        <Card elevation={4} sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #f3e8ff 0%, #dcfce7 100%)' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            ¡Excelente trabajo!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No hay actividades pendientes de revisión en este momento.
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 6, borderRadius: 4 }}>
          <Table size="small" sx={{ minWidth: 400 }}>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(90deg, #7C3AED 0%, #22C55E 100%)' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>Imagen</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Libro</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Actividad</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Fecha de envío</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((activity) => (
                <TableRow key={activity.id} hover sx={{ transition: 'background 0.2s', '&:hover': { background: '#f3e8ff' } }}>
                  <TableCell>
                    <Avatar 
                      variant="rounded" 
                      src={activity.image_url} 
                      alt={`Actividad ${activity.id}`} 
                      sx={{ width: 56, height: 56, cursor: 'pointer', border: '2px solid #e5e7eb' }}
                      onClick={() => window.open(activity.image_url, '_blank')}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`Libro ${activity.book_number}`} 
                      color="primary" 
                      variant="outlined" 
                      size="small" 
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`Actividad ${activity.activity_number}`} 
                      color="secondary" 
                      variant="outlined" 
                      size="small" 
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(activity.submitted_at).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(activity.submitted_at).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={activity.status === 'pending' ? 'Pendiente' : activity.status === 'approved' ? 'Aprobado' : 'Rechazado'} 
                      color={activity.status === 'pending' ? 'warning' : activity.status === 'approved' ? 'success' : 'error'} 
                      size="small" 
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    {activity.status === 'pending' ? (
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApprove(activity.id)}
                          disabled={processingActivity === activity.id}
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        >
                          {processingActivity === activity.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            '✅ Aprobar'
                          )}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleReject(activity.id)}
                          disabled={processingActivity === activity.id}
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        >
                          {processingActivity === activity.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            '❌ Rechazar'
                          )}
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {activity.reviewer_name ? `Revisado por ${activity.reviewer_name}` : 'Revisado'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminActivitiesList; 